/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * kCoreHandlerText - kCore's value, string, name, url and array helpers.
 * Split out of kCoreHandler.ts (2026-10-19, owner: smaller files for the reuse reviewer). The bodies are
 * moved VERBATIM except that `this` is the explicit first parameter `Self` (private/protected members are
 * read as Self["Name"]); kCore keeps every member as a same-signature delegating member.
 */

import { getCoreUtils } from "./kCoreErrorRegistry";
import type { kCore } from "./kCoreHandler";
import { nz, nb, n0, kNotEmptyString, kNotNullOrUndefined } from "./kCoreHandlerValues";

export class kCoreHandlerText {
    // DOCUMENTED EXCEPTION: moved body of kCore.Nz - its gate, try/catch and handler label "kCore.Nz" stay verbatim, so error reports are unchanged.
    public static Nz = (Self: typeof kCore, value: any): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try { return nz(value); }
        catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kCore.Nz"); return ""; }
    };

    // DOCUMENTED EXCEPTION: moved body of kCore.Nb - its gate, try/catch and handler label "kCore.Nb" stay verbatim, so error reports are unchanged.
    public static Nb = (Self: typeof kCore, value: any): boolean => {
        if (getCoreUtils().KErrorMode) { return false; }
        try { return nb(value); }
        catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kCore.Nb"); return false; }
    };

    // DOCUMENTED EXCEPTION: moved body of kCore.N0 - its gate, try/catch and handler label "kCore.N0" stay verbatim, so error reports are unchanged.
    public static N0 = (Self: typeof kCore, value: any): number => {
        if (getCoreUtils().KErrorMode) { return 0; }
        try { return n0(value); }
        catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kCore.N0"); return 0; }
    };

    // DOCUMENTED EXCEPTION: moved body of kCore.Nt - its gate, try/catch and handler label "kCore.Nt" stay verbatim, so error reports are unchanged.
    public static Nt = (Self: typeof kCore, value: any): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            if (Self.KNotEmptyString(value)) { return value; }
            return "";
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kCore.Nt"); return ""; }
    };

    // DOCUMENTED EXCEPTION: moved body of kCore.BooleanToString - its gate, try/catch and handler label "kCore.BooleanToString" stay verbatim, so error reports are unchanged.
    public static BooleanToString = (Self: typeof kCore, value: boolean): string => {
        if (getCoreUtils().KErrorMode) { return "false"; }
        try {
            if (value) { return "true"; }
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kCore.BooleanToString"); }
        return "false";
    };

    // DOCUMENTED EXCEPTION: moved body of kCore.KNotEmptyString - its gate, try/catch and handler label "kCore.KNotEmptyString" stay verbatim, so error reports are unchanged.
    public static KNotEmptyString = (Self: typeof kCore, varToCheck: any): boolean => {
        if (getCoreUtils().KErrorMode) { return false; }
        try { return kNotEmptyString(varToCheck); }
        catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kCore.KNotEmptyString"); return false; }
    };

    public static KNotInteger = (Self: typeof kCore, varToCheck: any): boolean => {
        if (getCoreUtils().KErrorMode) { return false; }
        try {
            if (Self.KNotNullOrUndefined(varToCheck)) {
                if (varToCheck === "") { return true; }
                if (isNaN(varToCheck)) { return true; }
                return false;
            }
            return false;
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kCoreHandlerText.KNotInteger"); return false; }
    };

    public static ParseBool = (Self: typeof kCore, str: any): boolean => {
        if (getCoreUtils().KErrorMode) { return false; }
        try {
            if (str === null) { return false; }
            if (typeof str === "boolean") { return str === true; }
            if (typeof str === "string") {
                if (str === "") { return false; }
                str = str.replace(/^\s+|\s+$/g, "");
                if (str.toLowerCase() === "true" || str.toLowerCase() === "yes" || str.toLowerCase() === 1) { return true; }
                str = str.replace(/,/g, ".");
                // eslint-disable-next-line no-useless-escape
                str = str.replace(/^\s*\-\s*/g, "-");
            }
            if (!isNaN(str)) { return parseFloat(str) !== 0; }
            return false;
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kCoreHandlerText.ParseBool"); return false; }
    };

    // ── String helpers ─────────────────────────────────────────────────────────
    public static ReplaceAll = (Self: typeof kCore, StringToFind: string, ReplaceWith: string, StringToSearchIn: string): string => {
        if (getCoreUtils().KErrorMode) { return StringToSearchIn; }
        try {
            if (!Self.KNotEmptyString(StringToSearchIn)) { return StringToSearchIn; }
            return StringToSearchIn.toString().split(StringToFind).join(ReplaceWith);
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kCoreHandlerText.ReplaceAll"); return StringToSearchIn; }
    };

    // Mild, GENERAL-PURPOSE accent fold: lowercase -> eszett to "ss" -> NFD strip of
    // combining marks (ä->a, ö->o, ü->u, é->e, à->a, ç->c ...). It does NOT collapse the
    // German digraphs (ae/oe/ue stay intact), so it is SAFE for arbitrary text search:
    // "Zürich" matches "zurich" and "naïve" matches "naive", while ordinary words that
    // merely contain those letter pairs ("Bluetooth", "queue", "Strasse") are preserved.
    // This is the normalization the general kSearch engine applies to every searchable
    // string. Pure string logic (no DOM) so it lives in kCore.
    // DOCUMENTED EXCEPTION: moved body of kCore.FoldAccents - its gate, try/catch and handler label "kCore.FoldAccents" stay verbatim, so error reports are unchanged.
    public static FoldAccents = (Self: typeof kCore, Value: string, PreserveBlank: boolean = false): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            if (PreserveBlank === false && !kNotEmptyString(Value)) { return ""; }
            if (PreserveBlank === true && !kNotNullOrUndefined(Value)) { return ""; }
            let Result: string = String(Value).toLowerCase();
            Result = Result.replace(/ß/g, "ss");
            Result = Result.normalize("NFD").replace(/[̀-ͯ]/g, "");
            return Result;
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kCore.FoldAccents"); return ""; }
    };

    // Fold a NAME to a canonical, accent-free, German-collapsed key so different spellings
    // of the same name converge. Builds on FoldAccents, then additionally collapses the
    // German digraphs and doubled-s so "ae/oe/ue/ss" land on the same single letter as
    // "ä/ö/ü/ß": "Lüthi"/"Luethi"/"luthi" and "Müller"/"Mueller"/"muller" all match. This
    // aggressive collapse is name-specific (it would corrupt ordinary words), so only the
    // people-name matchers (kSearchItemsCore.NameMatches / NameScore) use it.
    // DOCUMENTED EXCEPTION: moved body of kCore.NormalizeName - its gate, try/catch and handler label "kCore.NormalizeName" stay verbatim, so error reports are unchanged.
    public static NormalizeName = (Self: typeof kCore, Value: string): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            let Result: string = Self.FoldAccents(Value);
            Result = Result.replace(/ae/g, "a").replace(/oe/g, "o").replace(/ue/g, "u").replace(/ss/g, "s");
            return Result;
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kCore.NormalizeName"); return ""; }
    };

    // Split a name (and optional email) into canonical normalized tokens: name parts on
    // whitespace + hyphen (so "Winterberger-Rudin" -> two tokens), plus the email local
    // part on . _ + - . Each token is NormalizeName-folded. Powers any-order matching.
    // DOCUMENTED EXCEPTION: moved body of kCore.NameTokens - its gate, try/catch and handler label "kCore.NameTokens" stay verbatim, so error reports are unchanged.
    public static NameTokens = (Self: typeof kCore, Name: string, Email?: string): string[] => {
        if (getCoreUtils().KErrorMode) { return []; }
        try {
            const Tokens: string[] = [];
            let Raw: string;
            const NameParts: string[] = nz(Name).split(/[\s-]+/);
            for (Raw of NameParts) {
                const Norm: string = Self.NormalizeName(Raw);
                if (Norm !== "") { Tokens.push(Norm); }
            }
            const Mail: string = nz(Email);
            if (Mail.indexOf("@") > -1) {
                const LocalParts: string[] = Mail.split("@")[0].split(/[._+-]+/);
                for (Raw of LocalParts) {
                    const NormMail: string = Self.NormalizeName(Raw);
                    if (NormMail !== "") { Tokens.push(NormMail); }
                }
            }
            return Tokens;
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kCore.NameTokens"); return []; }
    };

    // DOCUMENTED EXCEPTION: moved body of kCore.ClearUnsafeFileChars - its gate, try/catch and handler label "kCore.ClearUnsafeFileChars" stay verbatim, so error reports are unchanged.
    public static ClearUnsafeFileChars = (Self: typeof kCore, OrgFileName: string): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            let Pair: string[];
            for (Pair of Self["_UnsafeFileChars"]) {
                OrgFileName = OrgFileName.split(Pair[0]).join(Pair[1]);
            }
            return OrgFileName;
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kCore.ClearUnsafeFileChars"); return ""; }
    };

    // Decode the JavaScript-style escape sequences SharePoint emits in OpenDocument
    // onclick handlers (\uXXXX, \xXX, \\, \/, \', \", \n, \r, \t). Anything else is
    // left intact so the function is safe to apply to arbitrary attribute strings.
    // DOCUMENTED EXCEPTION: moved body of kCore.UnescapeJsString - its gate, try/catch and handler label "kCore.UnescapeJsString" stay verbatim, so error reports are unchanged.
    public static UnescapeJsString = (Self: typeof kCore, Value: string): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            if (Value === null || Value === undefined) { return ""; }
            const Str: string = String(Value);
            return Str.replace(/\\u([0-9a-fA-F]{4})|\\x([0-9a-fA-F]{2})|\\([\\\/'"nrt])/g, Self.UnescapeJsStringReplacer);
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kCore.UnescapeJsString"); return String(Value); }
    };

    // DOCUMENTED EXCEPTION: moved body of kCore.UnescapeJsStringReplacer - its gate, try/catch and handler label "kCore.UnescapeJsStringReplacer" stay verbatim, so error reports are unchanged.
    public static UnescapeJsStringReplacer = (Self: typeof kCore, _Match: string, U: string, X: string, S: string): string => {
        if (getCoreUtils().KErrorMode) { return _Match; }
        try {
            if (U !== undefined && U !== "") { return String.fromCharCode(parseInt(U, 16)); }
            if (X !== undefined && X !== "") { return String.fromCharCode(parseInt(X, 16)); }
            if (S === "n") { return "\n"; }
            if (S === "r") { return "\r"; }
            if (S === "t") { return "\t"; }
            if (S === "\\") { return "\\"; }
            if (S === "/")  { return "/"; }
            if (S === "'")  { return "'"; }
            if (S === '"')  { return '"'; }
            return _Match;
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kCore.UnescapeJsStringReplacer"); return _Match; }
    };

    // DOCUMENTED EXCEPTION: moved body of kCore.TruncateText - its gate, try/catch and handler label "kCore.TruncateText" stay verbatim, so error reports are unchanged.
    public static TruncateText = (Self: typeof kCore, sourceString: string, length: number): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            if (sourceString === undefined || sourceString === null || sourceString === "") { return ""; }
            let truncatedString: string = sourceString.substring(0, length);
            if (sourceString.length > length) { truncatedString += "..."; }
            return truncatedString;
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kCore.TruncateText"); return ""; }
    };

    // DOCUMENTED EXCEPTION: moved body of kCore.EncodeSpecialCharacter - its gate, try/catch and handler label "kCore.EncodeSpecialCharacter" stay verbatim, so error reports are unchanged.
    public static EncodeSpecialCharacter = (Self: typeof kCore, stringval: string): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            if (stringval !== null && stringval !== "") {
                stringval = stringval
                    .replace(/>/g, "&gt;")
                    .replace(/</g, "&lt;")
                    .replace(/"/g, "&quot;")
                    .replace(/\\/g, "&#92;")
                    .replace(/&amp;/g, "&");
            }
            return stringval;
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kCore.EncodeSpecialCharacter"); return ""; }
    };

    // DOCUMENTED EXCEPTION: moved body of kCore.DecodeSpecialCharacter - its gate, try/catch and handler label "kCore.DecodeSpecialCharacter" stay verbatim, so error reports are unchanged.
    public static DecodeSpecialCharacter = (Self: typeof kCore, stringval: string): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            if (stringval !== null && stringval !== "") {
                stringval = stringval
                    .replace(/&gt;/g, ">")
                    .replace(/&lt;/g, "<")
                    .replace(/&quot;/g, '"')
                    .replace(/&#92;/g, "\\")
                    .replace(/&amp;/g, "&");
            }
            return stringval;
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kCore.DecodeSpecialCharacter"); return ""; }
    };

    // DOCUMENTED EXCEPTION: moved body of kCore.RemoveEndSlash - its gate, try/catch and handler label "kCore.RemoveEndSlash" stay verbatim, so error reports are unchanged.
    public static RemoveEndSlash = (Self: typeof kCore, str: string): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            if (Self.KNotEmptyString(str)) {
                if (str.substring(str.length - 1) === "/") {
                    str = str.substring(0, str.length - 1);
                }
            }
            return str;
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kCore.RemoveEndSlash"); return ""; }
    };

    /** DOCUMENTED EXCEPTION: pure native name projection; caller owns faults and reporting.
     * Preserve first/last trimmed-space tokens, or two characters from the original single name.
     * Existing GetInitials remains gated and retains its first-N-word defaults.
     */
    public static InitialsOfName = (Self: typeof kCore, Name: string): string => {
        const Parts: string[] = Name.trim().split(" ");
        if (Parts.length >= 2) { return (Parts[0][0] + Parts[Parts.length - 1][0]).toUpperCase(); }
        return Name.substring(0, 2).toUpperCase();
    };

    // DOCUMENTED EXCEPTION: moved body of kCore.GetInitials - its gate, try/catch and handler label "kCore.GetInitials" stay verbatim, so error reports are unchanged.
    public static GetInitials = (Self: typeof kCore, str: string, initialCount = 2): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            let initials = "";
            if (Self.KNotNullOrUndefined(str)) {
                const varParts: string[] = str.split(" ");
                const strCount: number = varParts.length;
                if (strCount < initialCount) { initialCount = strCount; }
                for (let i = 0; i < initialCount; i++) {
                    initials += varParts[i].substring(0, 1);
                }
            }
            if (initials === "") { initials = str.substring(0, 1); }
            return initials.toUpperCase();
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kCore.GetInitials"); return ""; }
    };

    public static ValidateEmail = (Self: typeof kCore, email: string): boolean => {
        if (getCoreUtils().KErrorMode) { return false; }
        try {
            return kCoreHandlerText._ValidateEmailBody(Self, email);
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kCoreHandlerText.ValidateEmail"); return false; }
    };

    // DOCUMENTED EXCEPTION: body of ValidateEmail, called only inside its guard and try/catch.
    private static _ValidateEmailBody = (Self: typeof kCore, email: string): boolean => {
        const reg = /^\w+([-+.']\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*$/;
        if (reg.test(email)) {
            const Parts: string[] = email.toLowerCase().split(".");
            if (Parts && Parts.length > 1) {
                const TLD: string | undefined = Parts.pop();
                if (TLD !== undefined) {
                    return Self["_ValidEmailTlds"].indexOf(TLD) > -1;
                }
                return false;
            }
            return false;
        }
        return false;
    };

    // ── URL helpers ────────────────────────────────────────────────────────────
    // DOCUMENTED EXCEPTION: moved body of kCore.EncodeUrl - its gate, try/catch and handler label "kCore.EncodeUrl" stay verbatim, so error reports are unchanged.
    public static EncodeUrl = (Self: typeof kCore, url: string): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            if (Self.KNotEmptyString(url)) {
                let urlParts: string[] = [];
                let i = 0;
                const fullUrl: string[] = url.split("//");
                if (fullUrl.length > 1) { urlParts = fullUrl[1].split("/"); }
                else { urlParts = fullUrl[0].split("/"); }
                for (i = 0; i < urlParts.length; i++) {
                    if (Self.KNotEmptyString(urlParts[i])) {
                        urlParts[i] = encodeURIComponent(urlParts[i]);
                    }
                }
                url = urlParts.join("/");
                if (fullUrl.length > 1) { url = fullUrl[0] + "//" + url; }
                return url;
            }
            return "";
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kCore.EncodeUrl"); return ""; }
    };

    // DOCUMENTED EXCEPTION: moved body of kCore.AppendQueryString - its gate, try/catch and handler label "kCore.AppendQueryString" stay verbatim, so error reports are unchanged.
    public static AppendQueryString = (Self: typeof kCore, url: string, paramName: string, paramValue: string): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            let queryString = "";
            if ((typeof paramName === "string" || typeof paramName === "number") &&
                (typeof paramValue === "string" || typeof paramValue === "number")) {
                url = Self.RemoveURLParameter(url, paramName);
                const queryStringParts: string[] = [];
                queryStringParts.push(paramName + "=" + paramValue);
                queryString = queryStringParts.join("&");
                if (queryString.length > 0) {
                    let firstSeperator = "&";
                    if (url.indexOf("?") === -1) { firstSeperator = "?"; }
                    queryString = firstSeperator + queryString;
                }
            }
            return url + queryString;
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kCore.AppendQueryString"); return ""; }
    };

    // DOCUMENTED EXCEPTION: moved body of kCore.RemoveURLParameter - its gate, try/catch and handler label "kCore.RemoveURLParameter" stay verbatim, so error reports are unchanged.
    public static RemoveURLParameter = (Self: typeof kCore, url: string, paramName: string): string => {
        if (getCoreUtils().KErrorMode) { return ""; }
        try {
            const urlparts: string[] = url.split("?");
            if (urlparts.length >= 2) {
                const prefix: string = encodeURIComponent(paramName) + "=";
                const pars: string[] = urlparts[1].split(/[&;]/g);
                for (let i: number = pars.length; i-- > 0; ) {
                    if (pars[i].lastIndexOf(prefix, 0) !== -1) { pars.splice(i, 1); }
                }
                let tail = "";
                if (pars.length > 0) { tail = "?" + pars.join("&"); }
                url = urlparts[0] + tail;
            }
            return url;
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kCore.RemoveURLParameter"); return ""; }
    };

    // ── Array / object helpers ─────────────────────────────────────────────────
    // DOCUMENTED EXCEPTION: moved body of kCore.ArrayMove - its gate, try/catch and handler label "kCore.ArrayMove" stay verbatim, so error reports are unchanged.
    public static ArrayMove = (Self: typeof kCore, arr: any[], old_index: number, new_index: number): any => {
        if (getCoreUtils().KErrorMode) { return false; }
        try {
            if (new_index >= arr.length) {
                let k = new_index - arr.length + 1;
                while (k--) { arr.push(undefined); }
            }
            arr.splice(new_index, 0, arr.splice(old_index, 1)[0]);
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kCore.ArrayMove"); return false; }
    };

    // DOCUMENTED EXCEPTION: moved body of kCore.Equals - its gate, try/catch and handler label "kCore.Equals" stay verbatim, so error reports are unchanged.
    public static Equals = (Self: typeof kCore, a: any, b: any): boolean => {
        if (getCoreUtils().KErrorMode) { return false; }
        try {
            if (a === b) { return true; }
            if (a instanceof Date && b instanceof Date) { return a.getTime() === b.getTime(); }
            if (!a || !b || (typeof a !== "object" && typeof b !== "object")) { return a === b; }
            if (a.prototype !== b.prototype) { return false; }
            const keys = Object.keys(a);
            if (keys.length !== Object.keys(b).length) { return false; }
            return keys.every(Self["_EqualsKeyFactory"](a, b));
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kCore.Equals"); return false; }
    };

    // DOCUMENTED EXCEPTION: builds a comparer closure and cannot throw; the comparer runs inside Equals' try/catch.
    public static _EqualsKeyFactory = (Self: typeof kCore, a: any, b: any): ((k: string) => boolean) => {
        const Compare = (k: string): boolean => Self.Equals(a[k], b[k]);
        return Compare;
    };

    public static GetObjectByTitle = (Self: typeof kCore, ObjectTitle: string, ObjectArray: any): any => {
        if (getCoreUtils().KErrorMode) { return false; }
        try {
            let CurrObject: any;
            for (CurrObject of ObjectArray) {
                if (CurrObject.Title === ObjectTitle) { return CurrObject; }
            }
            return null;
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kCoreHandlerText.GetObjectByTitle"); return null; }
    };

    // DOCUMENTED EXCEPTION: moved body of kCore.GetIndexFromID - its gate, try/catch and handler label "kCore.GetIndexFromID" stay verbatim, so error reports are unchanged.
    public static GetIndexFromID = (Self: typeof kCore, ID: number, ObjectArray: any[]): number => {
        if (getCoreUtils().KErrorMode) { return 0; }
        try {
            let CurrObject: any;
            for (let index = 0; index < ObjectArray.length; index++) {
                CurrObject = ObjectArray[index];
                if (CurrObject.ID === ID) { return index; }
            }
            return 0;
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kCore.GetIndexFromID"); return 0; }
    };

    // DOCUMENTED EXCEPTION: moved body of kCore.ObjectNamesArray - its gate, try/catch and handler label "kCore.ObjectNamesArray" stay verbatim, so error reports are unchanged.
    public static ObjectNamesArray = (Self: typeof kCore, ObjectArray: any[], OnlyNonHidden?: boolean): string[] => {
        if (getCoreUtils().KErrorMode) { return []; }
        try {
            const Results: string[] = [];
            let CurrObject: any = null;
            let CurrVisible = true;
            for (CurrObject of ObjectArray) {
                if (OnlyNonHidden === true) {
                    CurrVisible = true;
                    if (CurrObject.Hide !== undefined) {
                        if (CurrObject.Hide === true) { CurrVisible = false; }
                    }
                    if (CurrVisible) {
                        if (CurrObject.Name !== undefined) { Results.push(CurrObject.Name); }
                        else { Results.push(CurrObject.Title); }
                    }
                } else {
                    if (CurrObject.Name !== undefined) { Results.push(CurrObject.Name); }
                    else { Results.push(CurrObject.Title); }
                }
            }
            return Results;
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kCore.ObjectNamesArray"); return []; }
    };

    public static ValidateValues = (Self: typeof kCore, cellValue: any, DefaultValue: any, ValueType: string): any => {
        if (getCoreUtils().KErrorMode) { return; }
        try {
            let Value: any = DefaultValue;
            if (Self.KNotNullOrUndefined(cellValue)) {
                if (ValueType === "Int") {
                    if (Self.KNotInteger(cellValue)) { Value = DefaultValue; }
                    else { Value = parseInt(cellValue); }
                } else if (ValueType === "Bool") {
                    Value = Self.ParseBool(cellValue);
                } else {
                    Value = cellValue;
                }
            }
            return Value;
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "kCoreHandlerText.ValidateValues"); return DefaultValue; }
    };
}
